import React, { useState, useRef } from "react";
import {
    Box,
    Button,
    Container,
    Heading,
    VStack,
    HStack,
    Text,
    Card,
    CardBody,
    CardHeader,
    IconButton,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    useToast,
    Badge,
    Alert,
    AlertIcon,
    AlertDescription,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
    Spinner,
} from "@chakra-ui/react";
import { FiPlus, FiTrash2, FiCreditCard, FiShield, FiStar } from "react-icons/fi";
import { useQuery, useMutation } from "@apollo/client";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { ModuleBreadcrumb } from "../../components/ModuleBreadcrumb";
import profileModuleConfig from "./moduleConfig";
import { getColor, brandConfig } from "../../brandConfig";
import { StripePaymentMethodSetup } from "./components/StripePaymentMethodSetup";
import { useAuth } from "../../contexts/AuthContext";
import {
    MY_PAYMENT_METHODS,
    GET_TENANT_STRIPE_PUBLIC_KEY,
    CREATE_SETUP_INTENT,
    CREATE_PAYMENT_METHOD,
    DELETE_PAYMENT_METHOD,
    SET_DEFAULT_PAYMENT_METHOD,
} from "./queries";

const PaymentMethods: React.FC = () => {
    const { isOpen: isAddModalOpen, onOpen: onAddModalOpen, onClose: onAddModalClose } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const cancelRef = useRef<HTMLButtonElement>(null);
    const toast = useToast();
    const { user } = useAuth();
    const [methodToDelete, setMethodToDelete] = useState<string | null>(null);
    const [setupClientSecret, setSetupClientSecret] = useState<string>("");
    const [isCreatingSetupIntent, setIsCreatingSetupIntent] = useState(false);

    // Consistent styling from brandConfig
    const bg = getColor("background.main");
    const cardGradientBg = getColor("background.cardGradient");
    const cardBorder = getColor("border.darkCard");
    const textPrimary = getColor("text.primaryDark");
    const textSecondary = getColor("text.secondaryDark");
    const textMuted = getColor("text.mutedDark");

    // Fetch user's payment methods
    const { data, loading, error, refetch } = useQuery(MY_PAYMENT_METHODS);

    // Fetch tenant's Stripe public key
    const { data: stripeKeyData, loading: stripeKeyLoading } = useQuery(GET_TENANT_STRIPE_PUBLIC_KEY);
    const stripePublicKey = stripeKeyData?.getTenantStripePublicKey;

    const [createSetupIntent] = useMutation(CREATE_SETUP_INTENT);
    const [createPaymentMethod] = useMutation(CREATE_PAYMENT_METHOD);
    const [deletePaymentMethod, { loading: deletingPaymentMethod }] = useMutation(DELETE_PAYMENT_METHOD);
    const [setDefaultPaymentMethod] = useMutation(SET_DEFAULT_PAYMENT_METHOD);

    const paymentMethods = data?.myPaymentMethods || [];

    const handleAddPaymentMethod = async () => {
        if (!user?.id) {
            toast({
                title: "Not authenticated",
                description: "Please log in to add a payment method.",
                status: "warning",
                duration: 3000,
            });
            return;
        }

        setIsCreatingSetupIntent(true);
        try {
            const { data } = await createSetupIntent({
                variables: { clientId: user.id }
            });
            setSetupClientSecret(data.createSetupIntent);
            onAddModalOpen();
        } catch (error: any) {
            toast({
                title: "Setup Error",
                description: error.message || "Failed to initialize card setup",
                status: "error",
                duration: 5000,
            });
        } finally {
            setIsCreatingSetupIntent(false);
        }
    };

    const handleStripeSuccess = async (paymentMethodId: string, customerId?: string) => {
        if (!user?.id) return;

        try {
            await createPaymentMethod({
                variables: {
                    input: {
                        clientId: user.id,
                        type: "CARD",
                        stripePaymentMethodId: paymentMethodId,
                        stripeCustomerId: customerId,
                        isDefault: paymentMethods.length === 0, // First card is default
                    }
                }
            });

            toast({
                title: "Card added",
                description: "Your payment method has been securely saved.",
                status: "success",
                duration: 3000,
            });

            handleModalClose();
            refetch();
        } catch (error: any) {
            toast({
                title: "Error saving card",
                description: error.message,
                status: "error",
                duration: 5000,
            });
        }
    };

    const handleStripeError = (error: string) => {
        toast({
            title: "Card setup failed",
            description: error,
            status: "error",
            duration: 5000,
        });
        setIsCreatingSetupIntent(false);
    };

    const handleDeleteClick = (methodId: string) => {
        setMethodToDelete(methodId);
        onDeleteOpen();
    };

    const handleDeleteConfirm = async () => {
        if (!methodToDelete) return;

        try {
            const { data } = await deletePaymentMethod({
                variables: { id: methodToDelete }
            });

            if (data?.deletePaymentMethod) {
                toast({
                    title: "Payment method removed",
                    status: "success",
                    duration: 3000,
                });
                refetch();
            } else {
                toast({
                    title: "Failed to delete",
                    description: "Could not remove payment method.",
                    status: "error",
                    duration: 3000,
                });
            }
        } catch (error: any) {
            toast({
                title: "Error",
                description: error.message,
                status: "error",
                duration: 5000,
            });
        }
        onDeleteClose();
    };

    const handleSetDefault = async (methodId: string) => {
        try {
            const { data } = await setDefaultPaymentMethod({
                variables: { id: methodId }
            });

            if (data?.setDefaultPaymentMethod) {
                toast({
                    title: "Default payment method updated",
                    status: "success",
                    duration: 3000,
                });
                refetch();
            }
        } catch (error: any) {
            toast({
                title: "Error",
                description: error.message,
                status: "error",
                duration: 5000,
            });
        }
    };

    const handleModalClose = () => {
        onAddModalClose();
        setSetupClientSecret("");
        setIsCreatingSetupIntent(false);
    };

    const getBrandIcon = (brand?: string) => {
        switch (brand?.toLowerCase()) {
            case "visa": return "V";
            case "mastercard": return "MC";
            case "amex": return "AX";
            default: return "";
        }
    };

    if (loading || stripeKeyLoading) {
        return (
            <Box bg={bg} minHeight="100vh" display="flex" flexDirection="column">
                <NavbarWithCallToAction />
                <ModuleBreadcrumb moduleConfig={profileModuleConfig} />
                <Container maxW="container.lg" py={8} flex="1" display="flex" alignItems="center" justifyContent="center">
                    <VStack spacing={4}>
                        <Spinner size="xl" color={textPrimary} />
                        <Text color={textSecondary}>Loading payment methods...</Text>
                    </VStack>
                </Container>
                <FooterWithFourColumns />
            </Box>
        );
    }

    return (
        <Box bg={bg} minHeight="100vh" display="flex" flexDirection="column">
            <NavbarWithCallToAction />
            <ModuleBreadcrumb moduleConfig={profileModuleConfig} />
            <Container maxW="container.lg" py={8} flex="1">
                <VStack spacing={6} align="stretch">
                    <Box>
                        <VStack spacing={4}>
                            <VStack align="start" spacing={2} width="100%">
                                <HStack spacing={3} align="center">
                                    <Heading size="lg" color={textPrimary}>
                                        Payment Methods
                                    </Heading>
                                    <Badge colorScheme="purple" fontSize="xs" px={2} py={0.5} borderRadius="md">CUSTOMER</Badge>
                                </HStack>
                                <Text color={textSecondary}>
                                    Manage your saved payment methods for subscriptions and purchases.
                                </Text>
                            </VStack>
                            <Box width="100%" display="flex" justifyContent={{ base: "stretch", md: "flex-end" }}>
                                <Button
                                    leftIcon={<FiPlus />}
                                    bg="white"
                                    color="black"
                                    _hover={{
                                        bg: "gray.100",
                                        transform: "translateY(-2px)"
                                    }}
                                    onClick={handleAddPaymentMethod}
                                    isLoading={isCreatingSetupIntent}
                                    loadingText="Setting up..."
                                    isDisabled={!stripePublicKey}
                                    boxShadow="0 2px 4px rgba(255, 255, 255, 0.1)"
                                    width={{ base: "100%", md: "auto" }}
                                    minW={{ md: "180px" }}
                                    flexShrink={0}
                                >
                                    Add Payment Method
                                </Button>
                            </Box>
                        </VStack>
                    </Box>

                    <Alert
                        status="info"
                        bg="rgba(59, 130, 246, 0.1)"
                        borderColor="rgba(59, 130, 246, 0.3)"
                        borderWidth="1px"
                    >
                        <AlertIcon color="#3B82F6" />
                        <AlertDescription>
                            <HStack>
                                <FiShield color="#3B82F6" />
                                <Text color={textSecondary}>
                                    Your payment information is encrypted and securely stored by Stripe. We never store your full card details.
                                </Text>
                            </HStack>
                        </AlertDescription>
                    </Alert>

                    {error && (
                        <Alert status="error">
                            <AlertIcon />
                            <AlertDescription>Error loading payment methods: {error.message}</AlertDescription>
                        </Alert>
                    )}

                    <VStack spacing={4} align="stretch">
                        {paymentMethods.length === 0 ? (
                            <Card
                                bg={cardGradientBg}
                                backdropFilter="blur(10px)"
                                boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
                                border="1px"
                                borderColor={cardBorder}
                            >
                                <CardBody textAlign="center" py={8}>
                                    <Box display="inline-block">
                                        <FiCreditCard size={48} color={textMuted} />
                                    </Box>
                                    <Text mt={4} color={textMuted}>
                                        No payment methods saved
                                    </Text>
                                    <Button
                                        mt={4}
                                        leftIcon={<FiPlus />}
                                        bg="white"
                                        color="black"
                                        _hover={{
                                            bg: "gray.100",
                                            transform: "translateY(-2px)"
                                        }}
                                        onClick={handleAddPaymentMethod}
                                        isLoading={isCreatingSetupIntent}
                                        isDisabled={!stripePublicKey}
                                    >
                                        Add Your First Payment Method
                                    </Button>
                                </CardBody>
                            </Card>
                        ) : (
                            paymentMethods.map((method: any) => (
                                <Card
                                    key={method.id}
                                    bg={cardGradientBg}
                                    backdropFilter="blur(10px)"
                                    boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
                                    border="1px"
                                    borderColor={method.isDefault ? brandConfig.colors.primary : cardBorder}
                                >
                                    <CardHeader borderBottom="1px" borderColor={cardBorder}>
                                        <HStack justify="space-between" flexWrap={{ base: "wrap", md: "nowrap" }} gap={2}>
                                            <HStack flex="1" minW="0">
                                                <Text fontSize="lg" flexShrink={0}>
                                                    <FiCreditCard />
                                                </Text>
                                                <VStack align="start" spacing={0} minW="0" flex="1">
                                                    <HStack flexWrap="wrap">
                                                        <Text fontWeight="bold" color={textPrimary} noOfLines={1}>
                                                            {method.cardDetails?.brand?.toUpperCase() || "CARD"} {getBrandIcon(method.cardDetails?.brand)} ending in {method.cardDetails?.last4 || "****"}
                                                        </Text>
                                                        {method.isDefault && (
                                                            <Badge
                                                                bg="rgba(34, 197, 94, 0.2)"
                                                                color="#22C55E"
                                                                border="1px solid"
                                                                borderColor="rgba(34, 197, 94, 0.3)"
                                                                flexShrink={0}
                                                            >Default</Badge>
                                                        )}
                                                    </HStack>
                                                    <Text color={textSecondary} fontSize={{ base: "xs", sm: "sm" }} noOfLines={2}>
                                                        {method.cardDetails?.expMonth && method.cardDetails?.expYear && (
                                                            `Expires ${String(method.cardDetails.expMonth).padStart(2, "0")}/${method.cardDetails.expYear}`
                                                        )}
                                                        {method.createdAt && ` | Added ${new Date(parseInt(method.createdAt)).toLocaleDateString()}`}
                                                    </Text>
                                                </VStack>
                                            </HStack>
                                            <IconButton
                                                aria-label="Delete payment method"
                                                icon={<FiTrash2 />}
                                                size="sm"
                                                variant="ghost"
                                                color="#EF4444"
                                                _hover={{ bg: "rgba(239, 68, 68, 0.2)", color: "#EF4444" }}
                                                onClick={() => handleDeleteClick(method.id)}
                                                isDisabled={method.isDefault && paymentMethods.length > 1}
                                            />
                                        </HStack>
                                    </CardHeader>
                                    {!method.isDefault && (
                                        <CardBody pt={3} pb={3}>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                leftIcon={<FiStar />}
                                                borderColor={cardBorder}
                                                color={textPrimary}
                                                _hover={{
                                                    borderColor: textSecondary,
                                                    bg: "rgba(255, 255, 255, 0.05)"
                                                }}
                                                onClick={() => handleSetDefault(method.id)}
                                            >
                                                Set as Default
                                            </Button>
                                        </CardBody>
                                    )}
                                </Card>
                            ))
                        )}
                    </VStack>
                </VStack>
            </Container>

            {/* Stripe Payment Method Setup Modal */}
            <Modal isOpen={isAddModalOpen} onClose={handleModalClose} size={{ base: "full", md: "lg" }} closeOnOverlayClick={false}>
                <ModalOverlay bg="rgba(0, 0, 0, 0.8)" />
                <ModalContent
                    bg={cardGradientBg}
                    backdropFilter="blur(10px)"
                    border="1px"
                    borderColor={cardBorder}
                >
                    <ModalHeader color={textPrimary} borderBottom="1px" borderColor={cardBorder}>
                        Add Payment Method
                    </ModalHeader>
                    <ModalCloseButton color={textSecondary} />
                    <ModalBody pb={6}>
                        {!stripePublicKey ? (
                            <Alert status="error">
                                <AlertIcon />
                                <AlertDescription>
                                    Stripe is not configured. Please contact support.
                                </AlertDescription>
                            </Alert>
                        ) : setupClientSecret && stripePublicKey ? (
                            <StripePaymentMethodSetup
                                clientSecret={setupClientSecret}
                                onSuccess={handleStripeSuccess}
                                onError={handleStripeError}
                                clientName={user?.fName ? `${user.fName} ${user.lName || ""}`.trim() : "You"}
                                stripePublicKey={stripePublicKey}
                            />
                        ) : (
                            <VStack spacing={4} py={4}>
                                <Spinner />
                                <Text color={textSecondary}>Setting up secure payment form...</Text>
                            </VStack>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>

            {/* Delete Confirmation Dialog */}
            <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={onDeleteClose}>
                <AlertDialogOverlay>
                    <AlertDialogContent bg={cardGradientBg} borderColor={cardBorder} borderWidth="1px">
                        <AlertDialogHeader color={textPrimary}>
                            Delete Payment Method
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            <Text color={textSecondary}>
                                Are you sure you want to remove this payment method? This action cannot be undone.
                            </Text>
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onDeleteClose} color={textPrimary} variant="ghost">
                                Cancel
                            </Button>
                            <Button
                                bg="#EF4444"
                                color="white"
                                _hover={{ bg: "#DC2626" }}
                                onClick={handleDeleteConfirm}
                                ml={3}
                                isLoading={deletingPaymentMethod}
                                loadingText="Deleting..."
                            >
                                Delete
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            <FooterWithFourColumns />
        </Box>
    );
};

export default PaymentMethods;
